import { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogInIcon } from "lucide-react";
import { ApiOauthRedirectResp, ApiVerifyPasswordReq, ApiVerifyPasswordResp } from '@shared/authApiTypes';
import { useAuth } from '@/hooks/auth';
import './cfxreLoginButton.css';
import { useLocation } from "wouter";
import { fetchWithTimeout } from '@/hooks/fetch';
import { processFetchError } from './errors';
import { ServerGlowIcon } from '@/components/serverIcon';
import { LocalStorageKey } from '@/lib/localStorage';


function HeaderNoServer() {
    return (
        <div className="fc-auth-server">
            <div className="fc-auth-server-copy">
                <strong>Willkommen bei FC Admin</strong>
                <span>Bitte anmelden, um fortzufahren.</span>
            </div>
        </div>
    )
}

function HeaderServerInfo() {
    const server = window.txConsts.server;
    if (!server || !server.name || (!server.game && !server.icon)) {
        return <HeaderNoServer />;
    }
    return (
        <div className="fc-auth-server">
            <ServerGlowIcon
                iconFilename={server.icon}
                serverName={server.name}
                gameName={server.game}
            />
            <div className="fc-auth-server-copy">
                <strong>{server.name}</strong>
                <span>Anmelden, um fortzufahren.</span>
            </div>
        </div>
    )
}


export enum LogoutReasonHash {
    NONE = '',
    LOGOUT = '#logout',
    EXPIRED = '#expired',
    UPDATED = '#updated',
    MASTER_ALREADY_SET = '#master_already_set',
    SHUTDOWN = '#shutdown',
}

export default function Login() {
    const { setAuthData } = useAuth();
    const usernameRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [isFetching, setIsFetching] = useState(false);
    const setLocation = useLocation()[1];

    const onError = (error: any) => {
        const { errorTitle, errorMessage } = processFetchError(error);
        setErrorMessage(`${errorTitle}:\n${errorMessage}`);
    }

    const onErrorResponse = (error: string) => {
        if (error === 'no_admins_setup') {
            setErrorMessage('Noch kein Adminkonto eingerichtet.\nWeiterleitung …');
            setLocation('/addMaster/pin');
        } else {
            setErrorMessage(error);
        }
    }

    const handleLogin = async () => {
        try {
            setIsFetching(true);
            const data = await fetchWithTimeout<ApiVerifyPasswordResp, ApiVerifyPasswordReq>(
                `/auth/password?uiVersion=${encodeURIComponent(window.txConsts.txaVersion)}`,
                {
                    method: 'POST',
                    body: {
                        username: usernameRef.current?.value ?? '',
                        password: passwordRef.current?.value ?? '',
                    },
                }
            );
            if ('error' in data) {
                if (data.error === 'refreshToUpdate') {
                    window.location.href = `/login${LogoutReasonHash.UPDATED}`;
                    window.location.reload();
                } else {
                    onErrorResponse(data.error);
                }
            } else {
                setAuthData(data);
            }
        } catch (error) {
            onError(error);
        } finally {
            setIsFetching(false);
        }
    }

    const handleRedirect = async () => {
        try {
            setIsFetching(true);
            const data = await fetchWithTimeout<ApiOauthRedirectResp>(
                `/auth/cfxre/redirect?origin=${encodeURIComponent(window.location.origin)}`
            );
            if ('error' in data) {
                onErrorResponse(data.error);
                setIsFetching(false);
            } else {
                window.location.href = data.authUrl;
            }
        } catch (error) {
            onError(error);
            setIsFetching(false);
        }
    }

    useEffect(() => {
        try {
            const rawLocalStorageStr = localStorage.getItem(LocalStorageKey.AuthCredsAutofill);
            if (rawLocalStorageStr) {
                const [user, pass] = JSON.parse(rawLocalStorageStr);
                usernameRef.current!.value = user ?? '';
                passwordRef.current!.value = pass ?? '';
            }
        } catch (error) {
            console.error('Username/Pass autofill failed', error);
        }
    }, []);

    useEffect(() => {
        const hash = window.location.hash;
        if (!hash) return;
        if (hash === LogoutReasonHash.LOGOUT) {
            setErrorMessage('Du wurdest abgemeldet.');
        } else if (hash === LogoutReasonHash.EXPIRED) {
            setErrorMessage('Deine Sitzung ist abgelaufen.');
        } else if (hash === LogoutReasonHash.UPDATED) {
            setErrorMessage('FC Admin wurde aktualisiert.\nBitte erneut anmelden.');
        } else if (hash === LogoutReasonHash.MASTER_ALREADY_SET) {
            setErrorMessage('Das Master-Konto ist bereits eingerichtet.');
        } else if (hash === LogoutReasonHash.SHUTDOWN) {
            setErrorMessage('Der FC-Admin-Core wurde beendet.\nBitte starte ihn erneut, um dich anzumelden.');
        }
        window.location.hash = '';
    }, []);

    return (
        <form
            onSubmit={(e) => { e.preventDefault(); handleLogin();}}
            className='fc-auth-form'
        >
            <HeaderServerInfo />

            {errorMessage && <div className="fc-notice danger whitespace-pre-wrap">
                {errorMessage}
            </div>}

            <div className="fc-field">
                <Label htmlFor="frm-login">Benutzername</Label>
                <Input
                    id="frm-login"
                    ref={usernameRef}
                    type="text"
                    placeholder="Benutzername"
                    autoCapitalize='off'
                    autoComplete='off'
                    required
                />
            </div>
            <div className="fc-field">
                <Label htmlFor="frm-password">Passwort</Label>
                <Input
                    id="frm-password"
                    ref={passwordRef}
                    type="password"
                    placeholder='Passwort'
                    autoCapitalize='off'
                    autoComplete='off'
                    required
                />
            </div>

            <div className="fc-auth-actions">
                <Button disabled={isFetching} className="fc-sheen">
                    {isFetching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <LogInIcon className="h-4 w-4" />
                    )} Anmelden
                </Button>
                <Button
                    type="button"
                    className="cfxrebtn"
                    variant='secondary'
                    disabled={isFetching}
                    onClick={handleRedirect}
                >
                    {isFetching ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <LogInIcon className="h-4 w-4" />
                    )} Mit Cfx.re anmelden
                </Button>
            </div>
        </form>
    );
}
